/**
 * Đọc danh sách khách dán từ Excel / tệp CSV (hạng mục 2.2 Đợt 2, ADR-089, kế hoạch
 * `docs/plans/stt-2-1-2-2-khach-nhap.md` mục 5).
 *
 * Hàm THUẦN, dùng CHUNG: web quầy chạy để xem trước tại chỗ, máy chủ chạy lại đúng các hàm này trên ô
 * thô để kiểm lần nữa (không tin trạng thái / số đã chuẩn hoá từ máy khách). An toàn cho trình duyệt:
 * không `node:*`, không thư viện ngoài (`pnpm check:web-safe`).
 *
 * Nguyên tắc: không bao giờ NÉM lỗi vì dữ liệu của người dùng — mỗi dòng ra một `DongSach` với `loi`
 * (dòng KHÔNG nhập) hoặc `chuY` (dòng VẪN nhập, máy đã sửa giúp). Điều gì máy không hiểu thì chép vào
 * "Lưu ý cố định" chứ không bỏ mất.
 */
import { chuanHoaLoiNhan } from './loi-nhan.ts'
import { chuanHoaSdtVnChiTiet, hienSdtVn } from './sdt.ts'
import { lamSachTenKhach, TEN_KHACH_TOI_DA } from './ten-khach.ts'
import { laNgayVnThat } from './time.ts'

export const NHAP_KHACH_TOI_DA_DONG = 2000
export const LUU_Y_TOI_DA = 300

export type CotNhap = 'ten' | 'sdt' | 'ngaySinh' | 'luuY' | 'lanCuoi'

export type MaDongNhap =
  // Lỗi: dòng KHÔNG nhập.
  | 'THIEU_TEN'
  | 'TRUNG_SO_TRONG_TEP'
  | 'TRUNG_TEN_KHONG_SO'
  | 'DA_CO_HO_SO'
  // Chú ý: dòng VẪN nhập, máy đã sửa giúp.
  | 'SO_CU_DOI_DAU'
  | 'SO_CU_THIEU_0'
  | 'SO_EXCEL_RUT_GON'
  | 'SO_MAY_BAN'
  | 'NHIEU_SO'
  | 'SO_SAI'
  | 'KHONG_CO_SO'
  | 'NGAY_SINH_SAI'
  | 'NGAY_SINH_THIEU_NAM'
  | 'LAN_CUOI_SAI'
  | 'LAN_CUOI_TUONG_LAI'
  | 'LUU_Y_DAI'
  | 'TEN_DAI'

/** Mã của dòng KHÔNG nhập (còn lại là "chú ý"). */
export const MA_LOI_DONG: ReadonlySet<MaDongNhap> = new Set<MaDongNhap>([
  'THIEU_TEN',
  'TRUNG_SO_TRONG_TEP',
  'TRUNG_TEN_KHONG_SO',
  'DA_CO_HO_SO',
])

export interface DongTho {
  ten: string | null
  sdt: string | null
  ngaySinh: string | null
  luuY: string | null
  lanCuoi: string | null
}

export interface DongSach {
  ten: string | null
  /** `+84…` đã chuẩn hoá, hoặc `null`. */
  sdt: string | null
  /** YYYY-MM-DD */
  ngaySinh: string | null
  luuY: string | null
  /** YYYY-MM-DD */
  lanCuoi: string | null
  loi: MaDongNhap | null
  chuY: MaDongNhap[]
}

// ---------------------------------------------------------------------------------------------
// Tách bảng
// ---------------------------------------------------------------------------------------------

type DauPhan = '\t' | ';' | ','
const UU_TIEN_DAU: readonly DauPhan[] = ['\t', ';', ',']

interface BanGhi {
  o: string[][]
  /** Số dòng gốc (1-based) nơi bản ghi BẮT ĐẦU. */
  dong: number[]
  /** Số dấu `"` nằm giữa ô không mở bằng `"` — dấu phân cách đoán sai thường để lại nhiều dấu lẻ. */
  leDau: number
}

/**
 * RFC 4180 cho MỌI dấu phân cách (Excel dán TSV cũng bọc ô có xuống dòng / tab bằng `"`): ô MỞ bằng `"` thì
 * `""` = một `"`, dấu phân cách và xuống dòng bên trong là chữ; ô không mở bằng `"` thì `"` là chữ thường.
 * `dau = null`: không tách cột (chỉ tách bản ghi).
 */
function phanTichBanGhi(van: string, dau: DauPhan | null): BanGhi {
  const o: string[][] = []
  const dong: number[] = []
  let hang: string[] = []
  let cell = ''
  let dongHienTai = 1
  let dongBatDau = 1
  let dauO = true
  let leDau = 0
  let i = 0
  const n = van.length
  while (i < n) {
    const c = van.charAt(i)
    if (dauO && c === '"') {
      i++
      while (i < n) {
        const q = van.charAt(i)
        if (q === '"') {
          if (van.charAt(i + 1) === '"') {
            cell += '"'
            i += 2
            continue
          }
          i++
          break
        }
        if (q === '\n') dongHienTai++
        cell += q
        i++
      }
      dauO = false
      continue
    }
    if (dau !== null && c === dau) {
      hang.push(cell)
      cell = ''
      dauO = true
      i++
      continue
    }
    if (c === '\n') {
      hang.push(cell)
      o.push(hang)
      dong.push(dongBatDau)
      hang = []
      cell = ''
      dongHienTai++
      dongBatDau = dongHienTai
      dauO = true
      i++
      continue
    }
    if (c === '"') leDau++
    cell += c
    dauO = false
    i++
  }
  if (cell !== '' || hang.length > 0) {
    hang.push(cell)
    o.push(hang)
    dong.push(dongBatDau)
  }
  return { o, dong, leDau }
}

const laHangRong = (h: readonly string[]): boolean => h.every((c) => c.trim() === '')

/**
 * Dò dấu phân cách trên ≤ 20 dòng không rỗng đầu: dấu phải làm ≥ nửa số dòng có từ 2 ô trở lên; trong các
 * dấu đạt, chọn dấu cho nhiều dòng CÙNG số cột nhất (bảng thật có số cột đều nhau), hoà thì dấu để lại ít dấu `"` lẻ hơn, rồi
 * ưu tiên `\t` > `;` > `,`.
 */
function doDauPhan(van: string): DauPhan | null {
  const mau = van.length > 40_000 ? van.slice(0, 40_000) : van
  let tot: DauPhan | null = null
  let diemTot = 0
  let leTot = 0
  for (const dau of UU_TIEN_DAU) {
    const b = phanTichBanGhi(mau, dau)
    const hang = b.o.filter((h) => !laHangRong(h)).slice(0, 20)
    const nhieuO = hang.filter((h) => h.length >= 2)
    if (hang.length === 0 || nhieuO.length * 2 < hang.length) continue
    const dem = new Map<number, number>()
    for (const h of nhieuO) dem.set(h.length, (dem.get(h.length) ?? 0) + 1)
    // Tỉ lệ dòng cùng số cột (không phải số tuyệt đối): dấu đoán sai hay CHẺ một ô có xuống dòng thành thêm dòng
    // ngắn → nhiều dòng hơn nhưng không đều hơn. Quy ra phần nghìn để so sánh số nguyên.
    const diem = Math.floor((Math.max(...dem.values()) * 1000) / hang.length)
    if (diem > diemTot || (diem === diemTot && b.leDau < leTot)) {
      tot = dau
      diemTot = diem
      leTot = b.leDau
    }
  }
  return tot
}

/** Bỏ BOM + NUL, đưa xuống dòng về `\n`, tách bảng theo dấu dò được. Cắt khoảng trắng hai đầu từng ô. */
export function tachBang(van: string): {
  o: string[][]
  dongGoc: number[]
  dauPhan: DauPhan | null
} {
  let s = van.charCodeAt(0) === 0xfeff ? van.slice(1) : van
  s = s.split(String.fromCharCode(0)).join('').replace(/\r\n?/g, '\n')
  const dauPhan = doDauPhan(s)
  const b = phanTichBanGhi(s, dauPhan)
  const o: string[][] = []
  const dongGoc: number[] = []
  for (let k = 0; k < b.o.length; k++) {
    const hang = (b.o[k] as string[]).map((c) => c.trim())
    if (laHangRong(hang)) continue
    o.push(hang)
    dongGoc.push(b.dong[k] as number)
  }
  return { o, dongGoc, dauPhan }
}

// ---------------------------------------------------------------------------------------------
// Số điện thoại trong một ô
// ---------------------------------------------------------------------------------------------

const CAT_O_THO = 40

const catChu = (s: string, n: number): string => {
  const kt = [...s]
  return kt.length <= n ? s : kt.slice(0, n).join('')
}

/** Ô số điện thoại dài hơn mức này là dữ liệu hỏng / ác ý — cắt trước khi tách dãy số (regex tách là O(n²)). */
const CAT_O_SO = 200

/** Bỏ `="…"` (Excel xuất số dạng công thức) và dấu `'` đứng đầu (ép chữ); cắt ở `CAT_O_SO` ký tự. */
function lamSachOSo(raw: string): string {
  let s = catChu(raw.trim(), CAT_O_SO * 2).trim()
  const m = /^=\s*"([^"]*)"$/.exec(s)
  if (m !== null) s = m[1] ?? ''
  return catChu(s.replace(/^['’`]+/, '').trim(), CAT_O_SO)
}

const soChuSo = (s: string): number => s.replace(/\D/g, '').length

const hopLe = (s: string): boolean => chuanHoaSdtVnChiTiet(s, { nhanThieuSo0Cu: true }).so !== null

/** Tách một dãy số dính nhau thành các số riêng (nếu chia được); không chia được → giữ nguyên. */
function tachDay(day: string, sau = 0): string[] {
  if (hopLe(day) || sau > 4) return [day]
  // Hai số dính nhau bằng " - ", "/", ",", ";", "|" (không chia được ở nhóm chữ số thì thử ở đây).
  const chia = day.split(/\s[-–—]\s|\s*[/,;|]\s*/)
  if (chia.length > 1)
    return chia.flatMap((p) => (soChuSo(p) >= 8 ? tachDay(p.trim(), sau + 1) : []))
  const nhom = day.split(/\s+/).filter((x) => x !== '')
  for (let k = nhom.length - 1; k >= 1; k--) {
    const dau = nhom.slice(0, k).join(' ')
    if (hopLe(dau)) {
      const con = nhom.slice(k).join(' ')
      return soChuSo(con) >= 8 ? [dau, ...tachDay(con, sau + 1)] : [dau]
    }
  }
  return [day]
}

/**
 * Một dãy giống số điện thoại: chữ số cách nhau bằng dấu cách, chấm, gạch (cả gạch dài của Word / Excel), gạch
 * dưới, gạch chéo, phẩy, ngoặc. Phải rộng bằng các cách người ta gõ số — dãy nào lọt khỏi đây sẽ bị chép NGUYÊN
 * VĂN vào "Lưu ý cố định" mà `cheSoTrongChu` không che được (soát bảo mật 11/10).
 */
const DAY_SO = /\+?\d[\d\s.\-–—_/,()]{7,}\d/g

/**
 * Viết lại các dãy ≥ 7 chữ số trong ô thô cho `cheSoTrongChu` che được: chữ số toàn khổ → ASCII; mọi ký tự không
 * phải chữ / số ngăn giữa các chữ số (≤ 3 ký tự) → MỘT dấu cách. Dùng cho đoạn chép ô thô vào lưu ý.
 */
function chuanDangSoTrongChu(s: string): string {
  let ascii = ''
  for (const ch of s) {
    const cp = ch.codePointAt(0) ?? 0
    ascii += cp >= 0xff10 && cp <= 0xff19 ? String.fromCharCode(cp - 0xff10 + 0x30) : ch
  }
  return ascii.replace(/\+?\d(?:[^\p{L}\d]{0,3}\d)+/gu, (m) =>
    soChuSo(m) >= 7 ? m.replace(/[^\d+]+/g, ' ') : m,
  )
}

/** Các dãy giống số điện thoại trong ô, theo thứ tự xuất hiện. */
function cacDaySo(s: string): string[] {
  const kq: string[] = []
  for (const m of s.matchAll(DAY_SO)) {
    for (const p of tachDay(m[0].trim())) kq.push(p)
  }
  return kq
}

/** Số máy bàn: bỏ 0 / 84 đầu thì còn 10 chữ số bắt đầu bằng 2 (`024 3826 1234`). */
function laMayBan(s: string): boolean {
  let d = s.replace(/\D/g, '')
  if (d.startsWith('0084')) d = d.slice(4)
  let n = d
  if (d.startsWith('84') && d.length >= 11) {
    n = d.slice(2)
    if (n.startsWith('0')) n = n.slice(1)
  } else if (d.startsWith('0')) n = d.slice(1)
  return n.length === 10 && n.startsWith('2')
}

interface KetQuaOSo {
  sdt: string | null
  chuY: MaDongNhap[]
  /** Câu máy thêm vào "Lưu ý cố định". */
  them: string[]
}

function docOSo(raw: string | null): KetQuaOSo {
  const s = raw === null ? '' : lamSachOSo(raw)
  if (s === '') return { sdt: null, chuY: ['KHONG_CO_SO'], them: [] }
  const copTho = `Số trong sổ cũ: ${catChu(chuanDangSoTrongChu(s), CAT_O_THO)}`
  if (/\d[.,]\d+E\+?\d+/i.test(s)) {
    return { sdt: null, chuY: ['SO_EXCEL_RUT_GON'], them: [copTho] }
  }
  const day = cacDaySo(s)
  // Số CHẮC CHẮN đọc được (kể cả số 11 chữ số cũ có tiền tố 0 / 84). Số 10 chữ số đầu 1 không tiền tố
  // ("1689123456", Excel mất số 0) chỉ là ĐOÁN — không bao giờ làm số của hồ sơ (vòng sửa 2.1/2.2, 11/10).
  const dau = day.find((d) => chuanHoaSdtVnChiTiet(d).so !== null)
  if (dau === undefined) {
    const doan = day.find((d) => hopLe(d))
    if (doan !== undefined) {
      const g = chuanHoaSdtVnChiTiet(doan, { nhanThieuSo0Cu: true })
      return {
        sdt: null,
        chuY: ['SO_CU_THIEU_0'],
        them: [
          `Số cũ có thể là ${hienSdtVn(g.so)} — sổ ghi ${catChu(chuanDangSoTrongChu(s), CAT_O_THO)}`,
        ],
      }
    }
    const mayBan = day.length > 0 ? day.some((d) => laMayBan(d)) : laMayBan(s)
    return { sdt: null, chuY: [mayBan ? 'SO_MAY_BAN' : 'SO_SAI'], them: [copTho] }
  }
  const r = chuanHoaSdtVnChiTiet(dau)
  const chuY: MaDongNhap[] = []
  if (r.doiDauSo) chuY.push('SO_CU_DOI_DAU')
  const them: string[] = []
  const khac = day.filter(
    (d) =>
      d !== dau && chuanHoaSdtVnChiTiet(d, { nhanThieuSo0Cu: true }).so !== r.so && soChuSo(d) >= 9,
  )
  if (khac.length > 0) {
    chuY.push('NHIEU_SO')
    them.push(`Số khác: ${catChu(chuanDangSoTrongChu(khac.join(' / ')), CAT_O_THO)}`)
  }
  return { sdt: r.so, chuY, them }
}

// ---------------------------------------------------------------------------------------------
// Tìm cột
// ---------------------------------------------------------------------------------------------

/** Bỏ dấu + chữ thường + bỏ mọi ký tự không phải a-z0-9; `đ → d`. */
function khoaCot(x: string): string {
  return x
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/đ/g, 'd')
    .replace(/[^a-z0-9]/g, '')
}

const VAI_THU_TU: readonly CotNhap[] = ['ten', 'sdt', 'ngaySinh', 'luuY', 'lanCuoi']

const KHOP_KHIT: Record<CotNhap, readonly string[]> = {
  ten: [
    'ten',
    'hoten',
    'hovaten',
    'tenkhach',
    'tenkhachhang',
    'khachhang',
    'khach',
    'tenkh',
    'name',
    'fullname',
  ],
  sdt: [
    'sdt',
    'sodt',
    'dt',
    'didong',
    'dienthoai',
    'sodienthoai',
    'phone',
    'mobile',
    'tel',
    'sdtzalo',
  ],
  ngaySinh: ['ngaysinh', 'sinhnhat', 'ns', 'namsinh', 'birthday', 'dob'],
  luuY: ['ghichu', 'luuy', 'note', 'notes', 'chuy'],
  lanCuoi: [
    'lancuoitoi',
    'lancuoi',
    'lantoicuoi',
    'ngaytoicuoi',
    'landencuoi',
    'ngaydencuoi',
    'lantoigannhat',
    'ngaygannhat',
    'lastvisit',
  ],
}

const KHOP_CHUA: Record<CotNhap, readonly string[]> = {
  ten: ['hoten', 'hovaten', 'tenkhach', 'khachhang'],
  sdt: ['dienthoai', 'sdt', 'phone', 'didong'],
  ngaySinh: ['ngaysinh', 'sinhnhat'],
  luuY: ['ghichu', 'luuy'],
  lanCuoi: ['lancuoi', 'gannhat'],
}

/** Ghép vai cho một hàng tiêu đề: khít trước, "chứa" sau; một cột một vai, một vai một cột. */
function ganVai(tieuDe: readonly string[]): Partial<Record<CotNhap, number>> {
  const khoa = tieuDe.map(khoaCot)
  const cot: Partial<Record<CotNhap, number>> = {}
  const daDung = new Set<number>()
  for (let c = 0; c < khoa.length; c++) {
    const k = khoa[c] as string
    if (k === '') continue
    for (const vai of VAI_THU_TU) {
      if (cot[vai] === undefined && KHOP_KHIT[vai].includes(k)) {
        cot[vai] = c
        daDung.add(c)
        break
      }
    }
  }
  for (let c = 0; c < khoa.length; c++) {
    const k = khoa[c] as string
    if (k === '' || daDung.has(c)) continue
    for (const vai of VAI_THU_TU) {
      if (cot[vai] === undefined && KHOP_CHUA[vai].some((x) => k.includes(x))) {
        cot[vai] = c
        daDung.add(c)
        break
      }
    }
  }
  if (cot.sdt === undefined) {
    const z = khoa.findIndex((k, c) => k === 'zalo' && !daDung.has(c))
    if (z >= 0) cot.sdt = z
  }
  return cot
}

const TIEU_DE_TOI_DA = 5

/**
 * Tìm dòng tiêu đề (trong ≤ 5 dòng không rỗng đầu) và ghép cột. Không có tiêu đề: đoán cột Số điện thoại
 * (≥ 50% ô là số) và cột Tên (cột còn lại đầu tiên có chữ cái); một cột duy nhất → `ten` = `sdt` = cột 0.
 */
export function doCot(o: string[][]): {
  cot: Partial<Record<CotNhap, number>>
  dongTieuDe: number | null
  boTren: number
  loi: 'KHONG_THAY_COT_TEN' | null
} {
  for (let r = 0; r < Math.min(TIEU_DE_TOI_DA, o.length); r++) {
    const hang = o[r] as string[]
    const cot = ganVai(hang)
    if (Object.keys(cot).length === 0) continue
    if (hang.some((c) => c !== '' && hopLe(lamSachOSo(c)))) continue
    return {
      cot,
      dongTieuDe: r,
      boTren: r,
      loi: cot.ten === undefined ? 'KHONG_THAY_COT_TEN' : null,
    }
  }
  const soCot = o.reduce((m, h) => Math.max(m, h.length), 0)
  if (soCot <= 1) {
    return o.length === 0
      ? { cot: {}, dongTieuDe: null, boTren: 0, loi: 'KHONG_THAY_COT_TEN' }
      : { cot: { ten: 0, sdt: 0 }, dongTieuDe: null, boTren: 0, loi: null }
  }
  let cotSdt: number | undefined
  let diemSdt = 0
  const coChuCai = (s: string) => /\p{L}/u.test(s)
  const thongKe: { khongRong: number; soSdt: number; coChu: number }[] = []
  for (let c = 0; c < soCot; c++) {
    let khongRong = 0
    let soSdt = 0
    let coChu = 0
    for (const h of o) {
      const x = (h[c] ?? '').trim()
      if (x === '') continue
      khongRong++
      if (cacDaySo(lamSachOSo(x)).some((d) => hopLe(d))) soSdt++
      if (coChuCai(x)) coChu++
    }
    thongKe.push({ khongRong, soSdt, coChu })
    if (khongRong > 0 && soSdt * 2 >= khongRong && soSdt > diemSdt) {
      cotSdt = c
      diemSdt = soSdt
    }
  }
  let cotTen: number | undefined
  for (let c = 0; c < soCot; c++) {
    const t = thongKe[c]
    if (c === cotSdt || t === undefined) continue
    if (t.khongRong > 0 && t.coChu * 2 >= t.khongRong) {
      cotTen = c
      break
    }
  }
  const cot: Partial<Record<CotNhap, number>> = {}
  if (cotTen !== undefined) cot.ten = cotTen
  if (cotSdt !== undefined) cot.sdt = cotSdt
  return {
    cot,
    dongTieuDe: null,
    boTren: 0,
    loi: cotTen === undefined ? 'KHONG_THAY_COT_TEN' : null,
  }
}

/** Lấy ô thô theo cột, từ hàng `tu`. Ô rỗng → `null`. Một cột chung tên + số → tách dãy số đầu tiên ra. */
export function layDongTho(
  o: string[][],
  cot: Partial<Record<CotNhap, number>>,
  tu: number,
): DongTho[] {
  const lay = (h: string[], c: number | undefined): string | null => {
    if (c === undefined) return null
    const x = (h[c] ?? '').trim()
    return x === '' ? null : x
  }
  const motCot = cot.ten !== undefined && cot.ten === cot.sdt
  const ra: DongTho[] = []
  for (let r = Math.max(0, tu); r < o.length; r++) {
    const h = o[r] as string[]
    let ten = lay(h, cot.ten)
    let sdt = lay(h, cot.sdt)
    if (motCot && ten !== null) {
      const m = new RegExp(DAY_SO.source).exec(ten)
      if (m !== null) {
        sdt = m[0].trim()
        const con = `${ten.slice(0, m.index)} ${ten.slice(m.index + m[0].length)}`
          .replace(/\s+/g, ' ')
          .replace(/^[\s\-–—:,.;]+|[\s\-–—:,.;]+$/g, '')
        ten = con === '' ? null : con
      } else sdt = null
    }
    ra.push({
      ten,
      sdt,
      ngaySinh: lay(h, cot.ngaySinh),
      luuY: lay(h, cot.luuY),
      lanCuoi: lay(h, cot.lanCuoi),
    })
  }
  return ra
}

// ---------------------------------------------------------------------------------------------
// Ngày
// ---------------------------------------------------------------------------------------------

type KieuNgay = 'dmy' | 'mdy'

/** Phần ngày (bỏ giờ phía sau: `12/05/2024 10:30`, `2024-05-12T00:00`). */
const phanNgayCuaO = (raw: string): string => raw.trim().split(/[T\s]/)[0] ?? ''

/** Có ô nào không thể là ngày/tháng kiểu Việt (tháng > 12) → bằng chứng kiểu tháng/ngày, và ngược lại. */
export function doanKieuNgay(o: readonly (string | null)[]): KieuNgay {
  let dmy = 0
  let mdy = 0
  for (const x of o) {
    if (x === null) continue
    const m = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/.exec(phanNgayCuaO(x))
    if (m === null) continue
    const a = Number(m[1])
    const b = Number(m[2])
    if (a > 12 && b <= 12) dmy++
    else if (b > 12 && a <= 12) mdy++
  }
  return mdy > 0 && dmy === 0 ? 'mdy' : 'dmy'
}

type KetQuaNgay =
  | { kq: 'ok'; ngay: string }
  | { kq: 'thieu-nam' }
  | { kq: 'tuong-lai' }
  | { kq: 'sai' }

const hai = (n: number): string => String(n).padStart(2, '0')

function docNgay(
  raw: string,
  kieu: KieuNgay,
  homNay: string,
  loai: 'sinh' | 'lan-cuoi',
): KetQuaNgay {
  const p = phanNgayCuaO(raw)
  let y: number
  let m: number
  let d: number
  let iso = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(p)
  let namHaiSo = false
  if (iso !== null) {
    y = Number(iso[1])
    m = Number(iso[2])
    d = Number(iso[3])
  } else {
    iso = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4}|\d{2})$/.exec(p)
    if (iso === null) {
      if (/^(\d{1,2})[-/.](\d{1,2})$/.test(p) || /^\d{4}$/.test(p)) return { kq: 'thieu-nam' }
      return { kq: 'sai' }
    }
    const a = Number(iso[1])
    const b = Number(iso[2])
    ;[d, m] = kieu === 'dmy' ? [a, b] : [b, a]
    const nam = iso[3] as string
    namHaiSo = nam.length === 2
    y = Number(nam)
  }
  const dang = (nam: number) => `${String(nam).padStart(4, '0')}-${hai(m)}-${hai(d)}`
  let ngay: string
  if (namHaiSo) {
    ngay = dang(2000 + y)
    // Ngày sinh 2 chữ số năm: 20yy nếu chưa ở tương lai, không thì 19yy.
    if (loai === 'sinh' && ngay > homNay) ngay = dang(1900 + y)
  } else ngay = dang(y)
  if (!laNgayVnThat(ngay)) return { kq: 'sai' }
  if (loai === 'sinh') {
    return ngay < '1900-01-01' || ngay > homNay ? { kq: 'sai' } : { kq: 'ok', ngay }
  }
  if (ngay < '2000-01-01') return { kq: 'sai' }
  return ngay > homNay ? { kq: 'tuong-lai' } : { kq: 'ok', ngay }
}

// ---------------------------------------------------------------------------------------------
// Lưu ý cố định
// ---------------------------------------------------------------------------------------------

/**
 * Một luật ghi cho cả 3 đường (thêm / sửa / nhập): `chuanHoaLoiNhan` (≤ 6 dòng, bỏ ký tự ẩn), cắt ở
 * `LUU_Y_TOI_DA` ký tự theo code point (không cắt đôi emoji), rỗng → `null`. `daCat` = đã cắt bớt chữ.
 */
export function chuanHoaLuuY(s: string | null): { luuY: string | null; daCat: boolean } {
  if (s === null) return { luuY: null, daCat: false }
  // Dấu kết hợp chồng ≥ 3 (zalgo) bị gỡ — như tên khách: không để một lưu ý "cao" cả màn hình hay vỡ giao diện.
  const sach = chuanHoaLoiNhan(s.replace(/\p{M}{3,}/gu, '').normalize('NFC'))
  if (sach === '') return { luuY: null, daCat: false }
  const kt = [...sach]
  if (kt.length <= LUU_Y_TOI_DA) return { luuY: sach, daCat: false }
  return { luuY: kt.slice(0, LUU_Y_TOI_DA).join('').trimEnd(), daCat: true }
}

// ---------------------------------------------------------------------------------------------
// Chuẩn hoá một dòng
// ---------------------------------------------------------------------------------------------

/**
 * Một dòng thô → một dòng sạch. Không bao giờ ném. `loi` khác `null` = dòng không nhập (hiện chỉ
 * `THIEU_TEN`; các lỗi còn lại do `xetTrungTrongTep` và máy chủ gán).
 */
export function chuanHoaDongNhap(
  d: DongTho,
  ctx: { homNay: string; kieuNgaySinh: KieuNgay; kieuLanCuoi: KieuNgay },
): DongSach {
  const chuY: MaDongNhap[] = []
  const them: string[] = []
  let loi: MaDongNhap | null = null

  const tenTho = d.ten ?? ''
  const ten = lamSachTenKhach(tenTho)
  if (ten === null) loi = 'THIEU_TEN'
  else if ([...tenTho.normalize('NFC').replace(/\s+/g, ' ').trim()].length > TEN_KHACH_TOI_DA) {
    chuY.push('TEN_DAI')
  }

  const so = docOSo(d.sdt)
  for (const m of so.chuY) chuY.push(m)
  for (const t of so.them) them.push(t)

  let ngaySinh: string | null = null
  if (d.ngaySinh !== null && d.ngaySinh.trim() !== '') {
    const r = docNgay(d.ngaySinh, ctx.kieuNgaySinh, ctx.homNay, 'sinh')
    if (r.kq === 'ok') ngaySinh = r.ngay
    else if (r.kq === 'thieu-nam') {
      chuY.push('NGAY_SINH_THIEU_NAM')
      them.push(`Sinh nhật: ${catChu(d.ngaySinh.trim(), CAT_O_THO)}`)
    } else {
      chuY.push('NGAY_SINH_SAI')
      them.push(`Ngày sinh trong sổ: ${catChu(d.ngaySinh.trim(), CAT_O_THO)}`)
    }
  }

  let lanCuoi: string | null = null
  if (d.lanCuoi !== null && d.lanCuoi.trim() !== '') {
    const r = docNgay(d.lanCuoi, ctx.kieuLanCuoi, ctx.homNay, 'lan-cuoi')
    if (r.kq === 'ok') lanCuoi = r.ngay
    else chuY.push(r.kq === 'tuong-lai' ? 'LAN_CUOI_TUONG_LAI' : 'LAN_CUOI_SAI')
  }

  // Câu máy thêm đứng TRƯỚC để không bị cắt mất ở 300 ký tự.
  const may = them.join('; ')
  const ghi = d.luuY === null ? '' : d.luuY.trim()
  const gop = may !== '' && ghi !== '' ? `${may}\n${ghi}` : may !== '' ? may : ghi
  const { luuY, daCat } = chuanHoaLuuY(gop === '' ? null : gop)
  if (daCat) chuY.push('LUU_Y_DAI')

  return { ten, sdt: so.sdt, ngaySinh, luuY, lanCuoi, loi, chuY }
}

// ---------------------------------------------------------------------------------------------
// Trùng trong tệp
// ---------------------------------------------------------------------------------------------

/** Bảng bỏ dấu GIỐNG HỆT hàm SQL `bo_dau()` (0055_tim_khach.sql — 134 ký tự). */
const BANG_DAU_TU =
  'àÀáÁạẠảẢãÃâÂầẦấẤậẬẩẨẫẪăĂằẰắẮặẶẳẲẵẴèÈéÉẹẸẻẺẽẼêÊềỀếẾệỆểỂễỄìÌíÍịỊỉỈĩĨòÒóÓọỌỏỎõÕôÔồỒốỐộỘổỔỗỖơƠờỜớỚợỢởỞỡỠùÙúÚụỤủỦũŨưƯừỪứỨựỰửỬữỮỳỲýÝỵỴỷỶỹỸđĐ'
const BANG_DAU_DEN =
  'aaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaeeeeeeeeeeeeeeeeeeeeeeiiiiiiiiiioooooooooooooooooooooooooooooooooouuuuuuuuuuuuuuuuuuuuuuyyyyyyyyyydd'

const BANG_DAU: ReadonlyMap<string, string> = (() => {
  const tu = [...BANG_DAU_TU]
  const den = [...BANG_DAU_DEN]
  const m = new Map<string, string>()
  for (let i = 0; i < tu.length; i++) m.set(tu[i] as string, den[i] ?? '')
  return m
})()

/**
 * Khoá so tên: CÙNG kết quả với SQL `regexp_replace(btrim(bo_dau(x)), '\s+', ' ', 'g')` — dùng để khớp
 * tên với hồ sơ không số đang có (test so với DB thật ở khach-nhap.int.test.ts).
 */
export function khoaTen(ten: string): string {
  let ra = ''
  for (const ch of ten.normalize('NFC')) ra += BANG_DAU.get(ch) ?? ch
  return ra
    .toLowerCase()
    .replace(/^ +| +$/g, '')
    .replace(/\s+/g, ' ')
}

/**
 * Trùng TRONG tệp: trùng số → giữ dòng đầu; hai dòng cùng không có số mà cùng `khoaTen` → giữ dòng đầu.
 * Dòng đã lỗi (thiếu tên…) không đăng ký và không bị coi là trùng. `voi` = chỉ số (0-based) dòng giữ lại.
 */
export function xetTrungTrongTep(
  ds: readonly DongSach[],
): ({ ma: 'TRUNG_SO_TRONG_TEP' | 'TRUNG_TEN_KHONG_SO'; voi: number } | null)[] {
  const theoSo = new Map<string, number>()
  const theoTen = new Map<string, number>()
  return ds.map((d, i) => {
    if (d.loi !== null || d.ten === null) return null
    if (d.sdt !== null) {
      const co = theoSo.get(d.sdt)
      if (co !== undefined) return { ma: 'TRUNG_SO_TRONG_TEP' as const, voi: co }
      theoSo.set(d.sdt, i)
      return null
    }
    const k = khoaTen(d.ten)
    const co = theoTen.get(k)
    if (co !== undefined) return { ma: 'TRUNG_TEN_KHONG_SO' as const, voi: co }
    theoTen.set(k, i)
    return null
  })
}
